import { css } from "@emotion/css";
import { fontWeight, space, themeTokens } from "@octopusdeploy/design-system-tokens";
import type { ServerTaskApprovalBffResponse } from "@octopusdeploy/octopus-server-client";
import { links } from "@octopusdeploy/portal-routes";
import React from "react";
import { EnvironmentChip, TeamChip } from "~/components/Chips/index";
import ExternalLink from "~/components/Navigation/ExternalLink/index";
import InternalLink from "~/components/Navigation/InternalLink/index";
import { DataTable, DataTableBody, DataTableHeader, DataTableHeaderColumn, DataTableRow, DataTableRowColumn } from "~/primitiveComponents/dataDisplay/DataTable/index";
interface ServerTaskApprovalsTableProps {
    serverTaskApprovals: ServerTaskApprovalBffResponse[];
}
export function ServerTaskApprovalsTable({ serverTaskApprovals }: ServerTaskApprovalsTableProps) {
    return (<DataTable>
            <DataTableHeader>
                <DataTableRow>
                    <DataTableHeaderColumn aria-label={"Approval State"}>State</DataTableHeaderColumn>
                    <DataTableHeaderColumn aria-label={"Change Request"}>Change Request</DataTableHeaderColumn>
                    <DataTableHeaderColumn aria-label={"Project"}>Project</DataTableHeaderColumn>
                    <DataTableHeaderColumn aria-label={"Environment"}>Environment</DataTableHeaderColumn>
                    <DataTableHeaderColumn aria-label={"Task State"}>Task State</DataTableHeaderColumn>
                    <DataTableHeaderColumn aria-label={"Approvers"}>Approvers</DataTableHeaderColumn>
                </DataTableRow>
            </DataTableHeader>
            <DataTableBody>
                {serverTaskApprovals.map((approval) => (<DataTableRow key={approval.Id}>
                        <DataTableRowColumn>{getApprovalStateDescription(approval)}</DataTableRowColumn>
                        <DataTableRowColumn>{getChangeLink(approval)}</DataTableRowColumn>
                        <DataTableRowColumn>
                            <InternalLink to={links.projectDashboardPage.generateUrl({ spaceId: approval.SpaceId, projectSlug: approval.Project.Id })}>
                                <div className={styles.approvalTaskName}>{approval.Project.DisplayName}</div>
                            </InternalLink>
                        </DataTableRowColumn>
                        <DataTableRowColumn>
                            <EnvironmentChip environmentName={approval.Environment.DisplayName}/>
                        </DataTableRowColumn>
                        <DataTableRowColumn>{approval.ExecutionState}</DataTableRowColumn>
                        <DataTableRowColumn>
                            {approval.Approvers.map((team) => ({ Id: team.Id, Name: team.DisplayName })).map((team) => (<React.Fragment>
                                    <TeamChip key={team.Id} team={team}/>{" "}
                                </React.Fragment>))}
                        </DataTableRowColumn>
                    </DataTableRow>))}
            </DataTableBody>
        </DataTable>);
}
function getApprovalStateDescription(approval: ServerTaskApprovalBffResponse) {
    if (approval.ApprovalState === "preapproval") {
        return `Pending ${approval.ApprovalsCount ?? 0} / ${approval.MinimumApproversRequired}`;
    }
    if (approval.ApprovalState === "approved") {
        return "Approved";
    }
    if (approval.ApprovalState === "postapproval") {
        return "Completed";
    }
    return "Unknown";
}
function getChangeLink(approval: ServerTaskApprovalBffResponse) {
    if (approval.IsExternal) {
        return (<ExternalLink href={approval.ExternalLink}>
                <div className={styles.approvalTaskName}>{approval.ExternalNumber}</div>
            </ExternalLink>);
    }
    return (<InternalLink to={links.taskPage.generateUrl({ taskId: approval.Id })}>
            <div className={styles.approvalTaskName}>View</div>
        </InternalLink>);
}
const styles = {
    approvalDetailsCell: css({
        verticalAlign: "middle",
    }),
    providers: css({
        width: "16rem",
    }),
    approvalRow: css({
        ":hover": {
            backgroundColor: themeTokens.color.menuList.background.hover,
        },
    }),
    status: css({
        fontWeight: fontWeight["400"],
        display: "flex",
        gap: space[8],
    }),
    provider: css({
        fontWeight: fontWeight["400"],
    }),
    approvalTaskName: css({
        fontWeight: fontWeight["500"],
    }),
    loading: css({
        display: "flex",
        justifyContent: "center",
        paddingBottom: space[16],
    }),
};
